import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, MapPin, Facebook, Instagram } from 'lucide-react';
import { restaurantInfo } from '../config/siteConfig';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(prev => {
        const next = window.scrollY > 50;
        return next === prev ? prev : next;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { name: 'Menu', id: 'menu' },
    { name: 'Specials', id: 'specials' },
    { name: 'Gallery', id: 'gallery' },
    { name: 'Live Music', id: 'live-music' },
    { name: 'About', id: 'about' },
    { name: 'Hours', id: 'hours' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-[70] transition-[background-color,box-shadow] duration-500 ${
          isScrolled
            ? 'bg-[#1a1612]/98 backdrop-blur-md shadow-lg'
            : 'bg-[#1a1612]'
        }`}
      >
        <div className="max-w-6xl mx-auto px-5">
          <div className="flex items-center justify-between h-16">
            {/* Left Logo - Circular Overhang (hidden when mobile menu open) */}
            <button 
              onClick={() => scrollToSection('hero')} 
              className={`absolute left-4 sm:left-8 top-2 group z-[10001] transition-opacity duration-300 ${
                isMobileMenuOpen ? 'opacity-0 pointer-events-none md:opacity-100 md:pointer-events-auto' : 'opacity-100'
              }`}
            >
              <div
                className="relative bg-[#1a1612] rounded-full border-4 border-[#C9A227]/30 shadow-xl transition-transform duration-500 logo-clear w-[130px] h-[130px]"
                style={{
                  transform: isScrolled ? 'scale(0.692)' : 'scale(1)',
                  transformOrigin: 'top left',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 0 20px rgba(201,162,39,0.1)',
                  overflow: 'hidden'
                }}
              >
                {/* Inner gold ring */}
                <div className="absolute inset-1 rounded-full border border-[#C9A227]/20 z-10 pointer-events-none" />
                
                <img 
                  src="https://customer-assets.emergentagent.com/job_elegant-satchmos/artifacts/5uip0vr0_680-removebg-preview.png"
                  alt="Satchmo's Bar & Grill"
                  className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                  style={{ transform: 'scale(1.32) translateX(-3%)' }}
                />
              </div>
            </button>

            {/* Spacer for logo */}
            <div className="w-[138px]" />

            {/* Nav Links - Desktop */}
            <div className="hidden md:flex items-center gap-6 flex-1 justify-center">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="relative text-[#FDF6E9]/70 hover:text-[#C9A227] font-medium transition-colors group"
                >
                  {link.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#C9A227] group-hover:w-full transition-all duration-300" />
                </button>
              ))}
            </div>

            {/* Order Button & Social - Desktop */}
            <div className="hidden md:flex items-center gap-4">
              {restaurantInfo.social.facebook && (
                <a 
                  href={restaurantInfo.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#FDF6E9]/70 hover:text-[#C9A227] transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {restaurantInfo.social.instagram && (
                <a 
                  href={restaurantInfo.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#FDF6E9]/70 hover:text-[#C9A227] transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              <a
                href={restaurantInfo.orderOnlineUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#C9A227] hover:bg-[#d4ad2e] text-[#1a1612] font-bold px-5 py-2 transition-all hover:shadow-lg hover:shadow-[#C9A227]/20 text-sm"
                style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}
              >
                ORDER ONLINE
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
              className="md:hidden text-[#FDF6E9] p-3 hover:text-[#C9A227] transition-colors rounded-md"
              style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8)', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        id="mobile-menu"
        role="dialog"
        aria-modal="true"
        aria-hidden={!isMobileMenuOpen}
        className={`fixed inset-0 z-[60] bg-[#1a1612] transition-all duration-500 md:hidden ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
      >
        <div className="flex flex-col items-center justify-center h-full gap-6 pt-20">
          <div className="w-32 h-32 bg-[#1a1612] rounded-full border-4 border-[#C9A227]/30 overflow-hidden mb-4"
            style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_elegant-satchmos/artifacts/5uip0vr0_680-removebg-preview.png"
              alt=""
              className="w-full h-full object-cover"
              style={{ transform: 'scale(1.25)' }}
            />
          </div>
          {navLinks.map((link, i) => (
            <button
              key={link.id}
              onClick={() => scrollToSection(link.id)}
              className="font-display text-3xl text-[#FDF6E9] hover:text-[#C9A227] tracking-wide transition-colors"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              {link.name.toUpperCase()}
            </button>
          ))}
          <a
            href={restaurantInfo.fullMenuPdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setIsMobileMenuOpen(false)}
            className="font-display text-3xl text-[#FDF6E9] hover:text-[#C9A227] tracking-wide transition-colors"
          >
            FULL MENU
          </a>

          {/* Location */}
          <a 
            href="https://www.google.com/maps/dir/?api=1&destination=13375+Olive+Blvd+Chesterfield+MO+63017"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-[#FDF6E9]/60 hover:text-[#C9A227] mt-4 transition-colors"
          >
            <MapPin className="w-5 h-5" />
            <span className="text-lg">{restaurantInfo.address}, {restaurantInfo.city}</span>
          </a>
          
          <a href={`tel:${restaurantInfo.phone.replace(/[^0-9]/g, '')}`} className="flex items-center gap-2 text-[#FDF6E9]/60 hover:text-[#C9A227] transition-colors">
            <Phone className="w-5 h-5" />
            <span className="text-lg">{restaurantInfo.phone}</span>
          </a>
          <div className="flex items-center gap-4 mt-2">
            {restaurantInfo.social.facebook && (
              <a 
                href={restaurantInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#FDF6E9]/60 hover:text-[#C9A227] transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-6 h-6" />
              </a>
            )}
            {restaurantInfo.social.instagram && (
              <a 
                href={restaurantInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#FDF6E9]/60 hover:text-[#C9A227] transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-6 h-6" />
              </a>
            )}
          </div>
          <a href={restaurantInfo.orderOnlineUrl} target="_blank" rel="noopener noreferrer" className="mt-6 bg-[#C9A227] hover:bg-[#d4ad2e] text-[#1a1612] font-bold px-10 py-4 text-xl transition-all" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
            ORDER ONLINE
          </a>
        </div>
      </div>
    </>
  );
};

export default Navbar;
